import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MessagesSquare, Plus, Send, Flag, CheckCircle2, RotateCcw, Building2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AttachPicker, AttachmentList } from "@/components/Attachments";

function NewConversationDialog({ open, onClose, isStaff, onCreated }) {
  const [owners, setOwners] = useState([]);
  const [props, setProps] = useState([]);
  const [form, setForm] = useState({ landlord_id: "", property_id: "none", subject: "", message: "", email_owner: true, attachments: [] });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm({ landlord_id: "", property_id: "none", subject: "", message: "", email_owner: true, attachments: [] });
    api.get("/properties").then((r) => setProps(r.data)).catch(() => {});
    if (isStaff) api.get("/landlords").then((r) => setOwners(r.data)).catch(() => {});
  }, [open, isStaff]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const shownProps = isStaff ? props.filter((p) => p.landlord_id === form.landlord_id) : props;
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/conversations", { ...form, property_id: form.property_id === "none" ? null : form.property_id, landlord_id: form.landlord_id || null });
      toast.success(isStaff && form.email_owner ? "Sent — the owner has been emailed" : "Message sent");
      onCreated(data.id); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const valid = form.subject.trim() && (form.message.trim() || form.attachments.length) && (!isStaff || form.landlord_id);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="new-conversation-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">{isStaff ? "Message an owner" : "Message the team"}</DialogTitle>
          <DialogDescription>Every message is kept here with its date and time.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {isStaff && (
            <div><Label className="overline">Owner</Label>
              <Select value={form.landlord_id} onValueChange={(v) => setForm((f) => ({ ...f, landlord_id: v, property_id: "none" }))}>
                <SelectTrigger data-testid="conversation-owner" className="mt-1.5"><SelectValue placeholder="Choose an owner" /></SelectTrigger>
                <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div><Label className="overline">Property (optional)</Label>
            <Select value={form.property_id} onValueChange={(v) => set("property_id", v)}>
              <SelectTrigger data-testid="conversation-property" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">General</SelectItem>
                {shownProps.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="overline">Subject</Label><Input data-testid="conversation-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} className="mt-1.5" /></div>
          <div><Label className="overline">Message</Label><Textarea data-testid="conversation-message" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} className="mt-1.5" /></div>
          <AttachPicker files={form.attachments} onChange={(v) => set("attachments", v)} testId="conversation-attach" />
          {isStaff && <label className="flex items-center gap-2 text-sm"><Switch data-testid="conversation-email-owner" checked={form.email_owner} onCheckedChange={(v) => set("email_owner", v)} />Email the owner with this note and a sign-in link</label>}
        </div>
        <DialogFooter><Button data-testid="conversation-submit" onClick={submit} disabled={saving || !valid}><Send className="h-4 w-4 mr-1.5" />Send</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConversationList({ items, activeId, onPick, isStaff }) {
  if (!items.length) return <p data-testid="conversations-empty" className="p-8 text-center text-sm text-muted-foreground">No conversations yet.</p>;
  return items.map((c) => {
    const unread = isStaff ? c.unread_staff : c.unread_owner;
    return (
      <button key={c.id} data-testid={`conversation-item-${c.id}`} onClick={() => onPick(c.id)}
        className={`w-full text-left px-4 py-3 border-b border-border transition-colors hover:bg-secondary/60 ${activeId === c.id ? "bg-secondary" : ""}`}>
        <div className="flex items-center gap-2">
          {unread && <span data-testid={`conversation-unread-${c.id}`} className="h-2 w-2 rounded-full bg-[#D98324] shrink-0" />}
          {c.kind === "expense_flag" && <Flag className="h-3.5 w-3.5 text-[#9E382A] shrink-0" />}
          <p className={`text-sm truncate flex-1 ${unread ? "font-semibold" : "font-medium"}`}>{c.subject}</p>
          {c.status === "resolved" && <CheckCircle2 className="h-3.5 w-3.5 text-[#2B4C3F] shrink-0" />}
        </div>
        <p className="text-xs text-muted-foreground truncate mt-0.5">{isStaff ? `${c.landlord_name} · ` : ""}{c.last_message?.by_name}: {c.last_message?.text}</p>
        <p className="text-[0.65rem] text-muted-foreground mt-0.5">{fmtDateTime(c.last_message_at)}</p>
      </button>
    );
  });
}

function Thread({ id, isStaff, onChanged }) {
  const nav = useNavigate();
  const [conv, setConv] = useState(null);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [emailOwner, setEmailOwner] = useState(true);
  const [sending, setSending] = useState(false);
  const end = useRef(null);
  const load = () => api.get(`/conversations/${id}`).then((r) => { setConv(r.data); onChanged(); window.dispatchEvent(new Event("conversations-changed")); }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { setConv(null); load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [conv?.messages?.length]);
  const send = async () => {
    setSending(true);
    try { await api.post(`/conversations/${id}/messages`, { text, attachments: files, email_owner: emailOwner }); setText(""); setFiles([]); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };
  const setStatus = async (status) => {
    try { await api.patch(`/conversations/${id}`, { status }); toast.success(status === "resolved" ? "Marked resolved" : "Reopened"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!conv) return <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>;
  return (
    <div className="flex flex-col h-full" data-testid="conversation-thread">
      <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="font-head font-bold" data-testid="conversation-thread-subject">{conv.subject}</p>
          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
            {isStaff && <span>{conv.landlord_name}</span>}
            {conv.property_id && <button data-testid="conversation-property-link" onClick={() => nav(`/properties/${conv.property_id}`)} className="underline inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{conv.property_title}</button>}
            {conv.kind === "expense_flag" && <span className="text-[#9E382A]">· Expense query</span>}
            <span>· {conv.status === "resolved" ? "Resolved" : "Open"}</span>
          </p>
        </div>
        {isStaff && (conv.status === "open"
          ? <Button size="sm" variant="outline" data-testid="conversation-resolve" onClick={() => setStatus("resolved")}><CheckCircle2 className="h-4 w-4 mr-1" />Mark resolved</Button>
          : <Button size="sm" variant="outline" data-testid="conversation-reopen" onClick={() => setStatus("open")}><RotateCcw className="h-4 w-4 mr-1" />Reopen</Button>)}
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-secondary/30">
        {conv.messages.map((m) => {
          const mine = isStaff ? m.by === "staff" : m.by === "owner";
          return (
            <div key={m.id} data-testid={`conversation-message-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-lg px-3.5 py-2.5 ${mine ? "bg-[#2B4C3F] text-white" : "bg-white border border-border"}`}>
                {m.text && <p className="text-sm whitespace-pre-wrap">{m.text}</p>}
                <AttachmentList items={m.attachments} light={mine} msgId={m.id} />
                <p className={`text-[0.65rem] mt-1 ${mine ? "text-white/70" : "text-muted-foreground"}`}>{m.by_name} · {m.by === "staff" ? "Team" : "Owner"} · {fmtDateTime(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={end} />
      </div>
      <div className="p-4 border-t border-border space-y-2">
        <Textarea data-testid="conversation-reply" rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a reply…" />
        <AttachPicker files={files} onChange={setFiles} testId="reply-attach" />
        <div className="flex items-center justify-between gap-2 flex-wrap">
          {isStaff ? <label className="flex items-center gap-2 text-xs text-muted-foreground"><Switch data-testid="conversation-reply-email" checked={emailOwner} onCheckedChange={setEmailOwner} />Email the owner</label> : <span />}
          <Button data-testid="conversation-send" onClick={send} disabled={sending || (!text.trim() && !files.length)}><Send className="h-4 w-4 mr-1.5" />Send</Button>
        </div>
      </div>
    </div>
  );
}

export default function Conversations() {
  const { user } = useAuth();
  const isStaff = user.role !== "landlord";
  const [params, setParams] = useSearchParams();
  const activeId = params.get("c");
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("open");
  const [newOpen, setNewOpen] = useState(false);
  const load = () => api.get("/conversations", { params: status === "all" ? {} : { status } }).then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (id) => setParams(id ? { c: id } : {});
  return (
    <div className="space-y-6" data-testid="conversations-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-accent flex items-center gap-1.5"><MessagesSquare className="h-3.5 w-3.5" />Messages</p>
          <h1 className="font-head text-3xl font-extrabold tracking-tight mt-1">Conversations</h1>
          <p className="text-sm text-muted-foreground mt-1">{isStaff ? "Two-way messages with owners, including expense queries. Everything is timestamped." : "Talk to our team. Every message is kept with its date and time."}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="conversation-filter" className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="new-conversation-button" onClick={() => setNewOpen(true)}><Plus className="h-4 w-4 mr-1.5" />New message</Button>
        </div>
      </div>
      <div className="grid md:grid-cols-[320px_1fr] bg-white border border-border rounded-md overflow-hidden min-h-[520px]">
        <div className={`border-r border-border overflow-y-auto max-h-[70vh] ${activeId ? "hidden md:block" : ""}`}>
          <ConversationList items={items} activeId={activeId} onPick={pick} isStaff={isStaff} />
        </div>
        <div className={`${activeId ? "" : "hidden md:block"} h-[70vh]`}>
          {activeId ? (<>
            <button data-testid="conversation-back" onClick={() => pick(null)} className="md:hidden text-xs underline px-5 pt-3">← All conversations</button>
            <Thread id={activeId} isStaff={isStaff} onChanged={load} />
          </>) : <p className="p-10 text-center text-sm text-muted-foreground">Pick a conversation to read it.</p>}
        </div>
      </div>
      <NewConversationDialog open={newOpen} onClose={() => setNewOpen(false)} isStaff={isStaff} onCreated={(id) => { load(); pick(id); }} />
    </div>
  );
}
